import { useState, type ComponentType, type SVGProps } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { CreditCardStatementIcon } from '../../../components/general/CustomIcons/CreditCardStatementIcon';
import Popup from '../../../components/general/Popup.tsx';
import { cn } from '../../../lib/utils.ts';
import type {
    DocumentAction,
    DocumentActionAvailability,
    DocumentActionBlocker,
} from '../utils/documentActions.ts';

type MenuAction = Exclude<DocumentAction, 'delete'>;

type ActionDefinition = {
    id: MenuAction;
    label: string;
    icon: ComponentType<SVGProps<SVGSVGElement>>;
};

const ACTIONS: ActionDefinition[] = [
    {
        id: 'mark_as_credit_card_statement',
        label: 'Markeer als creditcard afschrift',
        icon: CreditCardStatementIcon,
    },
];

export const BLOCKER_LABELS: Record<DocumentActionBlocker, string> = {
    unsaved_changes: 'Sla je wijzigingen eerst op',
    processing: 'Het document wordt nog verwerkt',
    matched: 'Ontkoppel eerst de match',
    booked: 'Het document is geboekt',
    fixed_asset: 'Ontkoppel eerst het vaste activum',
};

type DocumentActionsMenuProps = {
    availability: Record<DocumentAction, DocumentActionAvailability>;
    onMarkAsCreditCardStatement: () => Promise<void>;
};

function ActionItem({ action, availability, onSelect }: {
    action: ActionDefinition;
    availability: DocumentActionAvailability;
    onSelect: () => void;
}) {
    const { label, icon: Icon } = action;
    const blocker = availability.blocker;
    return (
        <MenuItem disabled={blocker !== null}>
            <button
                type="button"
                onClick={onSelect}
                className={cn(
                    'flex w-full items-start gap-2 px-3 py-2 text-left text-sm',
                    'text-gray-700 data-[focus]:bg-gray-100',
                    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
                )}
            >
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <span className="flex flex-col">
                    <span>{label}</span>
                    {blocker && <span className="text-xs text-gray-500">{BLOCKER_LABELS[blocker]}</span>}
                </span>
            </button>
        </MenuItem>
    );
}

export function DocumentActionsMenu({
    availability,
    onMarkAsCreditCardStatement,
}: DocumentActionsMenuProps) {
    const [confirming, setConfirming] = useState<MenuAction | null>(null);
    const [isBusy, setIsBusy] = useState(false);
    const handlers: Record<MenuAction, () => Promise<void>> = {
        mark_as_credit_card_statement: onMarkAsCreditCardStatement,
    };
    const visibleActions = ACTIONS.filter((action) => availability[action.id].visible);

    if (visibleActions.length === 0) {
        return null;
    }

    const confirm = async (action: MenuAction) => {
        setIsBusy(true);
        try {
            await handlers[action]();
        } catch {
            // The drawer service already showed the failure to the user.
        } finally {
            setIsBusy(false);
            setConfirming(null);
        }
    };

    return (
        <>
            <Menu as="div" className="relative inline-block shrink-0 text-left">
                <MenuButton
                    aria-label="Documentacties"
                    title="Documentacties"
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition-[background-color,color,transform] duration-150 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary active:scale-[0.97] data-[open]:bg-gray-50 data-[open]:text-gray-800"
                >
                    <EllipsisVerticalIcon className="h-4 w-4" aria-hidden="true" />
                </MenuButton>
                <MenuItems
                    transition
                    anchor={{ to: 'bottom end', gap: 4 }}
                    className="z-50 w-72 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                    {visibleActions.map((action) => (
                        <ActionItem
                            key={action.id}
                            action={action}
                            availability={availability[action.id]}
                            onSelect={() => setConfirming(action.id)}
                        />
                    ))}
                </MenuItems>
            </Menu>
            <Popup
                open={confirming === 'mark_as_credit_card_statement'}
                onClose={() => setConfirming(null)}
                onConfirm={() => confirm('mark_as_credit_card_statement')}
                title="Markeren als creditcard afschrift"
                variant="default"
                confirmButtonDisplayLabel="Bevestigen"
                confirmButtonDisabled={isBusy}
                confirmOnEnter
            >
                <div className="space-y-3 text-sm text-gray-700">
                    <p>Weet je zeker dat je dit document wilt markeren als creditcard afschrift?</p>
                    <p>
                        Alle huidige gegevens van dit document worden verwijderd: de gescande gegevens en de ingevulde
                        velden en regels. Dit kun je niet ongedaan maken.
                    </p>
                </div>
            </Popup>
        </>
    );
}
